import React, { useRef, useState } from 'react';
import { Sparkles, Loader2, Upload, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { API } from '@/components/resource-library/resourceLibraryApi';
import { DOC_OPTIONS, SUBJECTS } from './courseGenMeta';

export const GeneratorForm = ({ onCreated }) => {
  const [subject, setSubject] = useState('Basic Science');
  const [customSubject, setCustomSubject] = useState('');
  const [year, setYear] = useState(9);
  const [docs, setDocs] = useState(DOC_OPTIONS.map((d) => d.key));
  const [notes, setNotes] = useState('');
  const [chapters, setChapters] = useState('');
  const [images, setImages] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syllabusFile, setSyllabusFile] = useState(null);
  const fileRef = useRef(null);

  const uploadSyllabus = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/api/course-generator/extract-syllabus`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }, body: fd });
    setUploading(false);
    if (!r.ok) return toast.error((await r.json().catch(() => ({}))).detail || 'Could not read that file');
    const d = await r.json();
    setNotes(d.text || '');
    setSyllabusFile({ name: file.name, pages: d.pages, chars: d.chars });
    toast.success(`Syllabus loaded: ${d.pages ? `${d.pages} pages, ` : ''}${d.chars.toLocaleString()} characters`);
  };

  const toggle = (k) => setDocs((d) => (d.includes(k) ? d.filter((x) => x !== k) : [...d, k]));
  const finalSubject = subject === '__custom' ? customSubject.trim() : subject;

  const submit = async () => {
    if (!finalSubject) return toast.error('Enter a subject name');
    if (!docs.length) return toast.error('Pick at least one document');
    setBusy(true);
    const r = await fetch(`${API}/api/course-generator/jobs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ subject: finalSubject, year_level: Number(year), doc_types: docs, syllabus_notes: notes || null, chapters: chapters ? Number(chapters) : null, include_images: images }),
    });
    setBusy(false);
    if (!r.ok) return toast.error((await r.json().catch(() => ({}))).detail || 'Could not start generation');
    toast.success('Generation started — this takes 10–25 minutes. You can leave this page.');
    onCreated();
  };

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm space-y-5" data-testid="course-generator-form">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="cg-subject">Subject</Label>
          <select id="cg-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-10 w-full rounded-md border bg-white px-3 text-sm" data-testid="cg-subject-select">
            {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
            <option value="__custom">Other…</option>
          </select>
          {subject === '__custom' && <Input placeholder="Subject name" value={customSubject} onChange={(e) => setCustomSubject(e.target.value)} data-testid="cg-subject-custom" />}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cg-year">Year level</Label>
          <select id="cg-year" value={year} onChange={(e) => setYear(e.target.value)} className="h-10 w-full rounded-md border bg-white px-3 text-sm" data-testid="cg-year-select">
            {[7, 8, 9, 10, 11, 12, 13].map((y) => <option key={y} value={y}>Year {y}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cg-chapters">Chapters (optional)</Label>
          <Input id="cg-chapters" type="number" min={4} max={16} placeholder="Let AI decide (8–13)" value={chapters} onChange={(e) => setChapters(e.target.value)} data-testid="cg-chapters-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Documents to generate</Label>
        <div className="grid gap-2 md:grid-cols-2">
          {DOC_OPTIONS.map((d) => (
            <label key={d.key} className="flex items-start gap-3 rounded-lg border p-3 hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={docs.includes(d.key)} onCheckedChange={() => toggle(d.key)} data-testid={`cg-doc-${d.key}`} />
              <span><span className="text-sm font-medium text-slate-900">{d.label}</span><br /><span className="text-xs text-slate-500">{d.hint}</span></span>
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="cg-notes">Syllabus / topic list (optional — upload the MoET syllabus or paste strands, sub-strands and indicator codes)</Label>
          <div className="flex items-center gap-2">
            {syllabusFile && <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="cg-syllabus-file"><FileCheck2 className="h-3.5 w-3.5" />{syllabusFile.name}</span>}
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => { uploadSyllabus(e.target.files?.[0]); e.target.value = ''; }} data-testid="cg-syllabus-input" />
            <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="cg-syllabus-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}Upload syllabus (PDF / Word)
            </Button>
          </div>
        </div>
        <Textarea id="cg-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Leave blank and Claude will reconstruct the Vanuatu syllabus for this subject and year." data-testid="cg-notes-textarea" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm"><Switch checked={images} onCheckedChange={setImages} data-testid="cg-images-switch" /> Include AI diagrams in the textbook (one per chapter)</label>
        <Button onClick={submit} disabled={busy} className="bg-indigo-700 hover:bg-indigo-800" data-testid="cg-generate-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}Generate course
        </Button>
      </div>
    </div>
  );
};
